import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Typography,
} from 'antd';
import { useState } from 'react';
import validator from 'validator';
import {
  Expression,
  GetSearchRequestByIndex,
  QueryExpressionInfo,
} from '@cbinsights/integratedsearchservice/integratedsearchservice';
import { DefaultOptionType } from 'antd/lib/select';
import { DataFeedForm, DataFeedFormField, SearchConfig } from './DataFeedForm';
import { getMultiSearch } from '../services/apis';

const baseUrl = () => {
  const env = process.env.AFS_SERVER_ENV;
  switch (env) {
    case 'local':
      return `app-dev.cbinsights.com/top-search`;
    case 'dev':
      return `app-dev.cbinsights.com/top-search`;
    case 'stg':
      return `app-staging.cbinsights.com/top-search`;
    default:
      return `app.cbinsights.com/top-search`;
  }
};

const configPath = [DataFeedFormField.filters, 'searchConfig'];
const urlPath = [DataFeedFormField.filters, 'searchURL'];
const tabPath = [...configPath, 'tab'];
const uuidPath = [...configPath, 'uuid'];
const topNOrgsPath = [...configPath, 'topNOrgs'];

export const validTabs = new Map([
  ['company', 1],
  ['investor', 2],
]);

const WATCHLIST = 133;
const KEYWORDS = 10000;
const TAG_TEAMS = 61;

const RESTRICTED_FILTERS = new Set([WATCHLIST, KEYWORDS, TAG_TEAMS]);

const hasRestrictedExpression = (
  expression: Expression | undefined
): boolean => {
  const key = expression?.operand?.key;
  if (key !== undefined && RESTRICTED_FILTERS.has(key)) return true;
  return expression?.expressions?.some(hasRestrictedExpression) ?? false;
};

const hasRestrictedFilter = (
  queryExpressions: QueryExpressionInfo[]
): boolean =>
  queryExpressions.some((qe) => hasRestrictedExpression(qe.expression));

const checkRestrictedFilters = async (tab: string, uuid: string) => {
  const multiSearchRequest: GetSearchRequestByIndex = {
    index: 0,
    company: undefined,
    investor: undefined,
    companyDeal: undefined,
    news: undefined,
    research: undefined,
    earnings: undefined,
    patents: undefined,
    includeTableKPIs: false,
  };

  if (tab === 'company') {
    multiSearchRequest.company = {
      pagination: {
        limit: 100,
        offset: 0,
      },
      sorts: [],
      columns: [],
    };
  }

  if (tab === 'investor') {
    multiSearchRequest.investor = {
      pagination: {
        limit: 100,
        offset: 0,
      },
      sorts: [],
      columns: [],
    };
  }

  let multiSearchResponse: Awaited<ReturnType<typeof getMultiSearch>>;
  try {
    multiSearchResponse = await getMultiSearch({
      multiSearchId: uuid,
      requests: [multiSearchRequest],
      skipSearch: true,
    });
  } catch (e: $TSFixMe) {
    const details: string = e?.response?.body?.error?.details ?? ''; // if the UUID doesn't exist, integratedsearchservice returns an error with 'missing from Dynamo' in the details
    if (details.includes('missing from Dynamo')) {
      throw new Error('Multi-search UUID not found.');
    }
    throw new Error('Failed to validate search. Please try again.');
  }

  if (hasRestrictedFilter(multiSearchResponse.queryExpressions)) {
    throw new Error(
      'This multi-search uses filters (Keywords, Watchlists, or Team Tags) that are not supported for data feeds'
    );
  }
};

const getURL = (url: string): URL | null => {
  try {
    return new URL(url);
  } catch {
    return null;
  }
};

const validateUuid = (uuid: string, errorMessage?: string) => {
  if (!validator.isUUID(uuid)) {
    throw new Error(errorMessage || `Invalid UUID`);
  }
};

const validateSearchURL = async (value: string) => {
  if (!value) {
    return;
  }

  const url = getURL(value);
  if (!url) {
    throw new Error(
      `Invalid URL. Expected format: https://${baseUrl()}?tab=<company|investor>&isid=0-<UUID>[&optionalParam=value...]`
    );
  }

  const tab = url.searchParams.get('tab');

  if (!tab || !validTabs.has(tab)) {
    throw new Error(
      `Invalid query parameter \`tab\`. Allowed values: ${Array.from(
        validTabs.keys()
      ).join(', ')}.`
    );
  }

  const isid = url.searchParams.get('isid');
  if (!isid) {
    throw new Error('Query parameter `isid` is required.');
  }

  if (!isid.startsWith('0-') || isid.length <= 2) {
    throw new Error(
      'Invalid query parameter `isid`. Expected format: 0-<UUID>'
    );
  }

  const uuid = isid.slice(2);
  validateUuid(uuid, 'Invalid UUID in `isid`');

  await checkRestrictedFilters(tab, uuid);
};

export const MultiSearchFilters = () => {
  const form = Form.useFormInstance<DataFeedForm>();
  const [editMode, setEditMode] = useState(false);

  const saveURLHandler = async () => {
    form.setFieldValue(configPath, {
      tab: undefined,
      uuid: undefined,
      topNOrgs: form.getFieldValue(topNOrgsPath),
    });

    try {
      await form.validateFields([urlPath]);
    } catch {
      return;
    }

    const searchURL = form.getFieldValue(urlPath);
    const url = getURL(searchURL);
    if (!url) return;

    const pTab = url.searchParams.get('tab');
    const uuid = url.searchParams.get('isid')!.slice(2);

    let tab: DefaultOptionType | undefined;
    if (pTab !== null) {
      tab = {
        label: pTab.charAt(0).toUpperCase() + pTab.slice(1),
        value: validTabs.get(pTab),
      };
    }

    const topNOrgs = form.getFieldValue(topNOrgsPath);
    const searchConfig: SearchConfig = {
      tab,
      uuid,
      topNOrgs,
    };
    form.setFieldValue(configPath, searchConfig);
  };

  const switchToEdit = () => {
    form.setFields([{ name: urlPath, value: undefined, errors: [] }]);
    if (!form.getFieldValue(tabPath)) {
      form.setFieldValue(tabPath, { label: 'Company', value: 1 });
    }
    setEditMode(true);
  };

  const switchToURL = () => {
    const tab = form.getFieldValue(tabPath);
    const uuid = form.getFieldValue(uuidPath);

    const doSwitch = () => {
      form.setFieldValue(configPath, {
        tab: undefined,
        uuid: undefined,
        topNOrgs: form.getFieldValue(topNOrgsPath),
      });
      form.setFields([
        { name: uuidPath, value: undefined, errors: [] },
        { name: urlPath, value: undefined, errors: [] },
      ]);
      setEditMode(false);
    };

    if (tab || uuid) {
      Modal.confirm({
        title: 'Switch to Search URL?',
        content: 'This will clear the current Tab and UUID values.',
        okText: 'Continue',
        cancelText: 'Cancel',
        onOk: doSwitch,
      });
    } else {
      doSwitch();
    }
  };

  const uuidRules = async (_: $TSFixMe, uuid: string) => {
    if (!uuid) {
      throw new Error('Multi-search UUID is required');
    }

    validateUuid(uuid);
    const tab: DefaultOptionType = form.getFieldValue(tabPath);
    await checkRestrictedFilters(String(tab.label).toLowerCase(), uuid);
  };

  const searchURLRules = async (_: $TSFixMe, value: string) => {
    if (value) {
      await validateSearchURL(value);
    }
  };

  return (
    <div>
      <Form.Item shouldUpdate className="mb-0">
        {() => {
          const tab: DefaultOptionType | undefined =
            form.getFieldValue(tabPath);
          const uuid: string | undefined = form.getFieldValue(uuidPath);

          return (
            <>
              {!editMode && (
                <Form.Item
                  name={urlPath}
                  rules={[{ validator: searchURLRules }]}
                  hasFeedback={!!form.getFieldValue(urlPath)}
                  validateTrigger={[]} // validation is triggered manually by saveURLHandler
                  label="Search URL"
                >
                  <Input
                    placeholder={`Paste a multi-search URL in the format: https://${baseUrl()}?tab=<company|investor>&isid=0-<uuid>[&optionalParam=value...]`}
                    onChange={saveURLHandler}
                  />
                </Form.Item>
              )}

              <Row gutter={16}>
                <Col span={8}>
                  {editMode ? (
                    <Form.Item name={tabPath} label="Tab">
                      <Select
                        labelInValue
                        showArrow
                        options={Array.from(validTabs).map(
                          ([label, value]) => ({
                            label:
                              label.charAt(0).toUpperCase() + label.slice(1),
                            value,
                          })
                        )}
                        placeholder="Select a tab"
                      />
                    </Form.Item>
                  ) : (
                    <>
                      <Form.Item name={tabPath} hidden>
                        <Input />
                      </Form.Item>
                      <div className="mb-4">
                        <Typography.Text type="secondary">Tab</Typography.Text>
                        <div>{tab?.label ?? '—'}</div>
                      </div>
                    </>
                  )}
                </Col>
                <Col span={8}>
                  {editMode ? (
                    <Form.Item
                      name={uuidPath}
                      label="UUID"
                      rules={[{ validator: uuidRules }]}
                      validateTrigger="onChange"
                      hasFeedback={!!uuid}
                    >
                      <Input placeholder="Enter multi-search UUID" />
                    </Form.Item>
                  ) : (
                    <>
                      <Form.Item name={uuidPath} hidden>
                        <Input />
                      </Form.Item>
                      <div className="mb-4">
                        <Typography.Text type="secondary">UUID</Typography.Text>
                        <div>{uuid ?? '—'}</div>
                      </div>
                    </>
                  )}
                </Col>
                <Col span={8}>
                  <Form.Item
                    name={topNOrgsPath}
                    label="Top X orgs"
                    tooltip="Limits the multi-search results to the top X organizations."
                    rules={[
                      {
                        type: 'number',
                        min: 1,
                        max: 10000,
                        message: 'Value must be between 1 and 10,000',
                      },
                    ]}
                  >
                    <InputNumber
                      className="w-full"
                      precision={0}
                      placeholder="Allowed range: 1 - 10,000"
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Button
                type="link"
                onClick={editMode ? switchToURL : switchToEdit}
                className="p-0"
              >
                {editMode ? 'Use Search URL' : 'Input manually'}
              </Button>
            </>
          );
        }}
      </Form.Item>
    </div>
  );
};
